import { useDispatch, useSelector } from 'react-redux';
import selectors from 'store/selectors';
import DIALOGS from 'utils/dialogIds';
import CustomDialogHeader from 'components/CustomDialog/DialogHeader/dialogHeader.cmp';
import { setDialog } from 'store/actions/dialogActions';
import CustomDialog from 'components/CustomDialog/customDialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BorderLinearProgress, ProjectIcon, ProjectProgrss } from './style';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { PROJECT_TYPES } from 'utils/globalVariable';
import UsersAndTeamsIcon from 'components/Icons/sidebarIcons/usersAndTeamsIcon';
import AssetsIcon from 'components/Icons/assetsIcon';
import CustomRolesIcon from 'components/Icons/customRolesIcon';
import Header from './header';
import TaskAndWorkOrderTable from 'components/DataTables/TaskAndWorkOrderTable';
import { getProjectTasks } from 'api/projectsApi';
import { useAssigneesUtils } from 'utils/CustomHooks/useAssigneesUtil';
import styled from '@emotion/styled';
import { useTaskEdit } from 'controller/useTaskEdit';

// Define Task interface
interface TaskAssignee {
  id: string;
  [key: string]: any;
}

interface Task {
  id: string;
  title?: string;
  description?: string;
  taskStatus?: string;
  LocationId?: string;
  Location?: any;
  TaskAssignees?: TaskAssignee[];
  assignees?: any[];
  [key: string]: any;
}

interface FilterState {
  locations: { value: string[] };
  statusFilters: { value: string[] };
  assigneeFilters: { value: string[] };
}

// Loader styled component
const LoaderWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '400px',
  width: '100%',
});

const ProjectDueTasks = () => {
  const dialogState = useSelector(selectors.getDialogState);

  const { handleEditDetailTask } = useTaskEdit();
  const locationsHash = useSelector(selectors.getLocationsHash);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksIds, setTasksIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    locations: { value: [] },
    statusFilters: { value: [] },
    assigneeFilters: { value: [] },
  });
  const dispatch = useDispatch();
  const { addNewAssignees } = useAssigneesUtils();

  const { open, data } = dialogState?.[DIALOGS?.NEXT_DUE_PROJECT] || [];
  const { project } = data;
  if (!project) return null;
  const { title, type, completionRate, completedTasksCount, totalSchedules } =
    project;
  const [allTasks, setAllTasks] = useState<Task[]>([]); // Store all tasks for filtering

  const handleClose = useCallback(() => {
    dispatch(
      setDialog({
        open: false,
        dialogId: DIALOGS.NEXT_DUE_PROJECT,
      }),
    );
  }, [dispatch]);

  const handleChangeSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleUpdateFilters = (updatedFilters: FilterState) => {
    setFilters(updatedFilters);
  };

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const data = await getProjectTasks(project.id);
      const taskIds: string[] = [];
      if (data?.activeTasks) {
        const transformedTasks = data?.activeTasks?.map((task: any) => {
          if (!task?.id?.includes('imaginary')) {
            taskIds.push(task?.id);
          }
          return {
            ...task,
            TaskAssignees: task?.assignees
              ? addNewAssignees(task?.assignees)
              : [],
            Location: task?.LocationId ? locationsHash[task?.LocationId] : null,
          };
        });
        setAllTasks(transformedTasks);
        setTasks(transformedTasks);
        setTasksIds(taskIds);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters and search on frontend
  const filteredTasks = useMemo(() => {
    if (!allTasks.length) return [];

    let result = [...allTasks];

    // Apply location filter
    if (filters.locations.value.length > 0) {
      result = result.filter(
        (task) =>
          task.LocationId && filters.locations.value.includes(task.LocationId),
      );
    }

    // Apply status filter
    if (filters.statusFilters.value.length > 0) {
      result = result.filter((task) => {
        // Tasks can have either status or taskStatus field
        const status = task.taskStatus || task.status;
        return status && filters.statusFilters.value.includes(status);
      });
    }

    // Apply assignee filter
    if (filters.assigneeFilters.value.length > 0) {
      result = result.filter((task) => {
        // Check if task has assignees either in TaskAssignees or assignees field
        const assignees = task.assignees || [];
        if (!assignees.length) return false;

        return assignees.some((assignee) =>
          filters.assigneeFilters.value.includes(assignee),
        );
      });
    }

    // Apply search term
    if (searchTerm.trim()) {
      const searchTermLower = searchTerm.toLowerCase().trim();
      result = result.filter(
        (task) =>
          (task.title && task.title.toLowerCase().includes(searchTermLower)) ||
          (task.description &&
            task.description.toLowerCase().includes(searchTermLower)),
      );
    }

    return result;
  }, [allTasks, filters, searchTerm]);

  useEffect(() => {
    setTasks(filteredTasks);
  }, [filteredTasks]);

  useEffect(() => {
    fetchTasks();
  }, [project.id]);

  const onRowClick = (id, type = 'detail') => {
    handleEditDetailTask(id, type);
  };

  const totalDisplayedTasks = tasks.length;

  return (
    <CustomDialog
      hideActions={true}
      header={
        <CustomDialogHeader onClose={handleClose}>
          <Stack direction={'row'} gap={1}>
            <Stack
              height={'100%'}
              direction={'row'}
              gap="12px"
              alignItems={'center'}
            >
              <ProjectIcon>
                {type === PROJECT_TYPES.userBasedProject ? (
                  <UsersAndTeamsIcon className="userIcon" />
                ) : type === PROJECT_TYPES.roleBasedProject ? (
                  <CustomRolesIcon className="roleIcon" />
                ) : (
                  <AssetsIcon className="assetIcon" />
                )}
              </ProjectIcon>
              <Box>
                <Typography
                  fontSize={'18px'}
                  fontWeight={'700'}
                  color={'#212121'}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  fontSize={'14px'}
                  fontWeight={'500'}
                  color={'#616161'}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Showing {totalDisplayedTasks} tasks
                </Typography>
              </Box>
            </Stack>
            <ProjectProgrss>
              {completedTasksCount} completed -{' '}
              {totalSchedules - completedTasksCount} to go
              <BorderLinearProgress
                variant="determinate"
                value={completionRate}
                progressBarBg={'#E7E7E7'}
                progressBarFg={'#04B88D'}
              />
            </ProjectProgrss>
          </Stack>
        </CustomDialogHeader>
      }
      dialogProps={{
        open,
        maxWidth: true,
        onClose: handleClose,
        fullScreen: false,
        sx: {
          '& .MuiDialog-paper': {
            maxWidth: 'calc(100vw - 200px)',
            width: '100%',
            maxHeight: 'calc(100vh - 200px)',
          },
        },
      }}
    >
      <Box p={'22px'}>
        <Header
          searchTerm={searchTerm}
          handleChangeSearch={handleChangeSearch}
          filters={filters}
          handleUpdateFilters={handleUpdateFilters}
          tasksIds={tasksIds}
        />

        {isLoading ? (
          <LoaderWrapper>
            <CircularProgress />
          </LoaderWrapper>
        ) : (
          <TaskAndWorkOrderTable
            rowData={tasks}
            onRowClick={onRowClick}
            columnConfig={{
              asset: false,
              recurringByEvery: false,
              timeSpent: false,
              startTime: false,
              ServiceType: false,
              description: false,
              cost: false,
              priority: false,
              actionCol: false,
              taskNumber: false,
            }}
          />
        )}
      </Box>
    </CustomDialog>
  );
};

export default ProjectDueTasks;
